import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type Player, type TeamWithRoster } from '../api';
import { AppShell } from '../components/AppShell';

export function TeamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [team, setTeam] = useState<TeamWithRoster | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);

  async function load() {
    if (!id) return;
    try {
      setErr(null);
      const data = await api.getTeam(id);
      setTeam(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function removePlayer(playerId: string) {
    if (!team) return;
    if (!confirm('Remove this player from the roster?')) return;
    await api.removePlayer(team.id, playerId);
    await load();
  }

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!team) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Link to="/teams" className="text-sm text-muted hover:text-text">
            ← Teams
          </Link>
        </div>

        <div className="flex items-start gap-4">
          <ColorSwatch
            primary={team.colorPrimary}
            secondary={team.colorSecondary}
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{team.name}</h1>
            {team.abbreviation && (
              <p className="mt-1 font-mono text-sm uppercase tracking-wider text-muted">
                {team.abbreviation}
              </p>
            )}
          </div>
        </div>

        <TeamLogoEditor team={team} onSaved={load} />

        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-xl font-semibold">Roster</h2>
            <p className="mt-1 text-sm text-muted">
              {team.players.length} player
              {team.players.length === 1 ? '' : 's'}
            </p>
          </div>
          {!showForm && !showImport && (
            <div className="flex gap-2">
              <button
                className="btn btn-secondary"
                onClick={() => setShowImport(true)}
              >
                ↑ Import CSV
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowForm(true)}
              >
                + Add player
              </button>
            </div>
          )}
        </div>

        {showForm && (
          <AddPlayerForm
            teamId={team.id}
            onDone={async () => {
              setShowForm(false);
              await load();
            }}
            onCancel={() => setShowForm(false)}
          />
        )}

        {showImport && (
          <ImportRosterForm
            teamId={team.id}
            onDone={async () => {
              setShowImport(false);
              await load();
            }}
            onCancel={() => setShowImport(false)}
          />
        )}

        {team.players.length === 0 && !showForm ? (
          <div className="card text-center text-muted">
            No players yet. Add one to get started.
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border">
            <table className="w-full divide-y divide-border text-sm">
              <thead className="bg-surface text-xs uppercase tracking-wider text-muted">
                <tr>
                  <th className="w-16 px-4 py-3 text-left">#</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Position</th>
                  <th className="w-20 px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {team.players.map((p) => (
                  <PlayerRow
                    key={p.id}
                    player={p}
                    onRemove={() => removePlayer(p.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PlayerRow({
  player,
  onRemove,
}: {
  player: Player;
  onRemove: () => void;
}) {
  return (
    <tr>
      <td className="px-4 py-3 font-mono text-text">
        {player.jerseyNumber || '—'}
      </td>
      <td className="px-4 py-3 font-medium text-text">{player.name}</td>
      <td className="px-4 py-3 text-muted">{player.position || '—'}</td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={onRemove}
          className="text-sm text-muted transition hover:text-red-400"
        >
          Remove
        </button>
      </td>
    </tr>
  );
}

function ColorSwatch({
  primary,
  secondary,
}: {
  primary: string | null;
  secondary: string | null;
}) {
  const a = primary ?? '#3b82f6';
  const b = secondary ?? a;
  return (
    <div
      className="h-16 w-16 shrink-0 rounded-xl border border-border"
      style={{
        background: `linear-gradient(135deg, ${a} 0%, ${a} 50%, ${b} 50%, ${b} 100%)`,
      }}
    />
  );
}

function TeamLogoEditor({
  team,
  onSaved,
}: {
  team: TeamWithRoster;
  onSaved: () => Promise<void> | void;
}) {
  const [logoUrl, setLogoUrl] = useState(team.logoUrl ?? '');
  const [imgFailed, setImgFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = logoUrl !== (team.logoUrl ?? '');

  function onUrlChange(value: string) {
    setLogoUrl(value);
    setImgFailed(false);
    setSaved(false);
    setErr(null);
  }

  function onPickFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') onUrlChange(reader.result);
    };
    reader.onerror = () => setErr('Could not read that file.');
    reader.readAsDataURL(file);
    // Allow re-selecting the same file later.
    e.target.value = '';
  }

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await api.updateTeam(team.id, { logoUrl: logoUrl || undefined });
      setSaved(true);
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Team logo</h2>
        <p className="mt-1 text-sm text-muted">
          PNG/SVG, keep under ~256 KB (data-URL logos ride in the live
          snapshot).
        </p>
      </div>
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-surface">
          {logoUrl && !imgFailed ? (
            <img
              src={logoUrl}
              alt="Team logo preview"
              className="h-full w-full object-contain"
              onError={() => setImgFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted">
              {imgFailed ? '⚠' : '—'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <input
            className="input"
            value={logoUrl}
            onChange={(e) => onUrlChange(e.target.value)}
            placeholder="https://example.com/logo.png"
          />
          <div className="flex items-center gap-3">
            <label className="btn btn-secondary cursor-pointer">
              Upload file…
              <input
                type="file"
                accept="image/png,image/svg+xml,image/*"
                className="hidden"
                onChange={onPickFile}
              />
            </label>
            {logoUrl && (
              <button
                type="button"
                className="text-sm text-muted transition hover:text-red-400"
                onClick={() => onUrlChange('')}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary"
          onClick={save}
          disabled={busy || !dirty}
        >
          {busy ? 'Saving…' : 'Save logo'}
        </button>
        {saved && !dirty && (
          <span className="text-sm text-emerald-300">Saved</span>
        )}
      </div>
    </div>
  );
}

function ImportRosterForm({
  teamId,
  onDone,
  onCancel,
}: {
  teamId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    skipped: number;
    errors: Array<{ line: number; reason: string; raw: string }>;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.importPlayers(teamId, csv);
      setResult(r);
      if (r.created > 0 && r.errors.length === 0) {
        // Clean import — close the form so the user sees the new players.
        onDone();
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_import');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h3 className="font-semibold">Import CSV roster</h3>
      <p className="text-sm text-muted">
        Paste a CSV with a header row. Required column:{' '}
        <span className="font-mono">name</span>. Optional columns:{' '}
        <span className="font-mono">jerseyNumber</span>,{' '}
        <span className="font-mono">position</span>. Quoted fields with
        commas are fine.
      </p>
      <pre className="overflow-x-auto rounded-lg border border-border bg-surface p-3 text-xs text-muted">
        {`name,jerseyNumber,position
Avery Stone,23,G
Jordan Reyes,11,F
"Davis, Jr.",5,C`}
      </pre>
      <textarea
        className="input min-h-[160px] font-mono text-sm"
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder="Paste your CSV here…"
        required
      />
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      {result && (
        <div className="rounded-lg border border-brand/30 bg-brand/5 px-4 py-3 text-sm">
          <div>
            <span className="font-semibold text-emerald-300">
              {result.created}
            </span>{' '}
            created
            {result.skipped > 0 && (
              <>
                ,{' '}
                <span className="font-semibold text-amber-300">
                  {result.skipped}
                </span>{' '}
                skipped
              </>
            )}
          </div>
          {result.errors.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-amber-200/80">
              {result.errors.slice(0, 10).map((er, i) => (
                <li key={i}>
                  line {er.line} — {er.reason}:{' '}
                  <span className="font-mono">{er.raw}</span>
                </li>
              ))}
              {result.errors.length > 10 && (
                <li>… and {result.errors.length - 10} more</li>
              )}
            </ul>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy || !csv.trim()}>
          {busy ? 'Importing…' : 'Import'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Close
        </button>
      </div>
    </form>
  );
}

function AddPlayerForm({
  teamId,
  onDone,
  onCancel,
}: {
  teamId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [position, setPosition] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.addPlayer(teamId, {
        name,
        jerseyNumber: jerseyNumber || undefined,
        position: position || undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'failed_to_add');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4">
      <h3 className="font-semibold">Add player</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="md:col-span-2">
          <label className="label">Jersey #</label>
          <input
            className="input font-mono"
            value={jerseyNumber}
            onChange={(e) => setJerseyNumber(e.target.value.slice(0, 10))}
            placeholder="23"
            maxLength={10}
          />
        </div>
        <div className="md:col-span-6">
          <label className="label">Name</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={1}
          />
        </div>
        <div className="md:col-span-4">
          <label className="label">Position</label>
          <input
            className="input"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            placeholder="G, F, C, QB, …"
            maxLength={50}
          />
        </div>
      </div>
      {err && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Adding…' : 'Add player'}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
